import axios from 'axios'
import { getActivePinia } from 'pinia'
import { useBrandStore } from '@/stores/brand'
import { useAppStore } from '@/stores/app'
import { Canvas, StaticCanvas } from 'fabric'
import AiApiClient from '@/utils/aiApiClient'
import s3Folders from '@/constants/s3Folders'
import { useProductStore } from '@/stores/product'
import { useCanvasStore } from '@/stores/canvas'
import { usePlacementStore } from '@/stores/placement'
import { useAssetStore } from '@/stores/asset'
import LogService from '@/services/LogService'
import { getAccessToken } from '@/api/idp'

const logService = new LogService()

import type { Product } from '@/types/product'
import { discountTypes } from '@/constants/canvas'
import { rgbStringToHex } from './colorHelper'
import designFlows from '@/constants/designFlows'
import { useUploadedCreativeStore } from '@/stores/uploadedCreative'
import { layerNames } from '@/constants/layers'

export async function downloadFilefromUrl(fileUrl: string, filename?: string) {
  const response = await axios.get(fileUrl, {
    responseType: 'blob',
  })
  const blob = response.data
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'download'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export async function uploadToS3(file: File, folder: string, fileName: string, fromAutofix: boolean = false, logoUpload: boolean = false): Promise<string> {
  if (!getActivePinia()) {
    throw new Error('Pinia is not initialized. Make sure app.use(pinia) is called.')
  }
  const brandStore = useBrandStore()
  const brandName = brandStore.selectedBrand?.name === undefined ? 'default' : brandStore.selectedBrand?.name
  const appStore = useAppStore()
  const uploadedCreativeStore = useUploadedCreativeStore()
  const data = new FormData()
  data.append('file', file)
  const filePath = `StunnrBE/${brandName}/${folder}/${fileName}`
  data.append('filePath', filePath)
  data.append('designType', appStore.selectedDesignFlow.value)

  if (logoUpload == true) {
    data.append('logoUpload', 'true')
  }

  if (fromAutofix) {
    if (appStore.selectedDesignFlow.value === designFlows.upload.value || appStore.selectedDesignFlow.value === designFlows.convert.value) {
      data.append('inputUrl', uploadedCreativeStore.uploadedCreativeUrl)
    }
  }
  const accessToken = getAccessToken()

  const aiApiClient = new AiApiClient(import.meta.env.VITE_SAM_API_URL)
  const response = await aiApiClient.fetchWithRetry(
    `bp/${appStore.selectedBusinessProfileId}/upload_image`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken || 'invalid_token'}`,
      },
      credentials: 'include',
      body: data,
    },
    true,
  )

  return response.url
}

export function getImageDimensionsFromFile(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.width, height: img.height })
    img.onerror = reject

    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string
      } else {
        reject('Failed to read file.')
      }
    }
    reader.readAsDataURL(file)
  })
}

export async function uploadFabricCanvasToS3(fabricCanvas: Canvas | StaticCanvas): Promise<string> {
  const dataUrl = fabricCanvas.toDataURL({ format: 'png', multiplier: 2 })

  const blobData = await fetch(dataUrl).then((res) => res.blob())

  const file = new File([blobData], `Generated-Creative-${Date.now()}.png`, { type: 'image/png' })
  const s3Url = await uploadToS3(file, s3Folders.generatedCreatives, file.name, true, false)

  return s3Url
}

export function getLuminance(r: number, g: number, b: number): number {
  const [R, G, B] = [r, g, b].map((c) => {
    const channel = c / 255
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

export function contrastRatio(l1: number, l2: number): number {
  return l1 > l2 ? (l1 + 0.05) / (l2 + 0.05) : (l2 + 0.05) / (l1 + 0.05)
}

export function hexToRgb(hex: string) {
  const cleanHex = hex.replace('#', '')
  if (cleanHex.length !== 6 && cleanHex.length !== 8) return null
  const bigint = parseInt(cleanHex.substring(0, 6), 16) // only first 6 for RGB
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
    a: cleanHex.length === 8 ? parseInt(cleanHex.substring(6, 8), 16) / 255 : 1, // optional alpha
  }
}

export function getContrastColor(bgColor: string, candidateColors: string[]): string {
  if (bgColor.includes('rgb')) {
    const hex = rgbStringToHex(bgColor)
    if (hex) {
      bgColor = hex
    }
  }
  if (!candidateColors.includes('#ffffff')) {
    candidateColors.push('#ffffff')
  }
  if (!candidateColors.includes('#000000')) {
    candidateColors.push('#000000')
  }

  const bgRgb = hexToRgb(bgColor)
  if (!bgRgb) throw new Error(`Invalid background color: ${bgColor}`)
  const bgLuminance = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b)

  let bestColor = candidateColors[0]
  let maxContrast = 0
  for (const color of candidateColors) {
    const rgb = hexToRgb(color)
    if (!rgb) continue
    const luminance = getLuminance(rgb.r, rgb.g, rgb.b)
    const contrast = contrastRatio(bgLuminance, luminance)
    if (contrast > maxContrast) {
      maxContrast = contrast
      bestColor = color
    }
  }
  return bestColor
}

// Randomize number. But final number should be in between min and max
export function getRandomNumber(value: number = 0, min: number = -10, max: number = 10): number {
  return Math.floor(Math.random() * (max - min + 1)) + min + value
}

// Function to get the Input data
const getInputDataForAdValidation = () => {
  const canvasStore = useCanvasStore()
  const productStore = useProductStore()
  const brandStore = useBrandStore()
  const placementStore = usePlacementStore()
  const headlineData = canvasStore.layers['Headline']?.text || ''
  const subheadlineData = canvasStore.layers['SubHeadline']?.text || ''

  const inputData = {
    brandName: brandStore.selectedBrand?.name || '',
    logoUrl: brandStore.selectedLogo
      ? brandStore.selectedLogo.logoUrl
      : '',
    productUrls: productStore.selectedProducts.map((product: Product) =>
      product.productUrl || '',
    ),
    backgroundTextureImage: placementStore.selectedBackgroundImage || '',
    primaryColor: brandStore.selectedColor || '',
    headline: headlineData,
    subheadline: subheadlineData,
    discountInfo: JSON.stringify({
      isEnabled: canvasStore.discountType !== discountTypes.none,
      type: canvasStore.discountType || '',
      variation: canvasStore.discountVariation || '',
      value: canvasStore.discountValue || '',
      couponCode: canvasStore.rawCouponCode?.toUpperCase() || '',
    }),
  }

  return inputData
}

// Function To replace the Products src(ImageBytes) in newCanvasJson to original Image Url
const replaceProductImagesInCanvasJson = (latestCanvasJson: any): any => {
  const productStore = useProductStore()
  const selectedProducts = productStore.selectedProducts
  if (!Array.isArray(latestCanvasJson?.objects)) return latestCanvasJson
  const updatedObjects = latestCanvasJson.objects.map((obj: any) => {
    const layerName = obj?.layerName
    if (typeof layerName === 'string' && layerName.startsWith('Product')) {
      const match = layerName.match(/^Product(\d+)$/)
      const index = match ? parseInt(match[1], 10) - 1 : -1
      const product = selectedProducts[index]

      if (index >= 0 && product) {
        return {
          ...obj,
          src: product.productUrl,
        }
      }
    }

    return obj
  })
  return {
    ...latestCanvasJson,
    objects: updatedObjects,
  }
}

export const logCreative = async (isFinal: boolean) => {
  try {
    const appStore = useAppStore()
    const placementStore = usePlacementStore()
    const canvasStore = useCanvasStore()
    const assetStore = useAssetStore()
    const bpId = appStore.selectedBusinessProfileId

    const inputData = getInputDataForAdValidation()
    const updatedCanvasJson = replaceProductImagesInCanvasJson(canvasStore.canvasJson)
    const sessionId = appStore.sessionId

    const requestData: any = {
      canvasJson: JSON.stringify(placementStore.selectedPublishedVariant?.canvasJson),
      newCanvasJson: JSON.stringify(updatedCanvasJson),
      guidelinesData: JSON.stringify(placementStore.selectedPlacement?.creativeSetGuidelines),
      creativeS3Url: assetStore.creativeS3Url,
      inputData: JSON.stringify(inputData),
      isFinal: isFinal,
      sessionId: sessionId,
    }

    const logData = JSON.stringify(requestData, null, 2)
    await logService.addLog(bpId, logData, isFinal)
  } catch (error) {
    console.log('Error logging activity:', error)
  }
}

export const moveToFront = (_eventData: any, transform: any): void => {
  const canvasStore = useCanvasStore()
  const target = transform.target
  const canvas = target?.canvas || canvasStore.fabricCanvas
  if (canvas && target) {
    const shadow = canvas
      .getObjects()
      .find(
        (o: any) =>
          o.layerId == target.layerId &&
          (o.layerName == 'Shadow' || o.layerName == 'AIShadow'),
      );
    if (shadow) {
      canvas.bringObjectToFront(shadow)
    }
    canvas.bringObjectToFront(transform.target)
    canvas.requestRenderAll()
  } else {
    console.error('Canvas or target not found for moveToFront')
  }
}

export const moveToBottom = (_eventData: any, transform: any): void => {
  const canvasStore = useCanvasStore()
  const target = transform.target
  const canvas = target?.canvas || canvasStore.fabricCanvas
  if (canvas && target) {
    const shadow = canvas
      .getObjects()
      .find(
        (o: any) =>
          o.layerId == target.layerId &&
          (o.layerName == 'Shadow' || o.layerName == 'AIShadow'),
      );

    const duoToneShapes = canvas
      .getObjects()
      .filter(
        (obj: any) =>
          obj.layerName == layerNames.BackgroundShape ||
          obj.layerName == layerNames.BackgroundTexture,
      )
    if (duoToneShapes.length) {
      canvas.moveObjectTo(transform.target, duoToneShapes.length)
      if (shadow) {
        canvas.moveObjectTo(shadow, duoToneShapes.length)
      }
    } else {
      canvas.sendObjectToBack(transform.target)
      if (shadow) {
        canvas.sendObjectToBack(shadow)
      }
    }
    canvas.requestRenderAll()
  } else {
    console.error('Canvas or target not found for moveToBottom')
  }
}

export const renderChevronIcon = (iconName: string) => {
  return function (ctx: any, left: any, top: any) {
    const size = 20
    ctx.save()
    ctx.translate(left, top)
    ctx.beginPath()
    ctx.roundRect(-size / 2, -size / 2, size, size, 4)
    ctx.fillStyle = '#ffffff'
    ctx.fill()
    ctx.fillStyle = 'black'
    if (iconName === 'up') {
      ctx.beginPath()
      ctx.moveTo(-6, 4)
      ctx.lineTo(0, -4)
      ctx.lineTo(6, 4)
      ctx.fill()
    } else {
      ctx.beginPath()
      ctx.moveTo(-6, -4)
      ctx.lineTo(0, 4)
      ctx.lineTo(6, -4)
      ctx.fill()
    }
    ctx.restore()
  }
}

export function hasGoodContrast(hex1: string, hex2: string, threshold = 4.5): boolean {
  const rgb1 = hexToRgb(hex1)
  const rgb2 = hexToRgb(hex2)
  if (!rgb1 || !rgb2) return false
  const l1 = getLuminance(rgb1?.r, rgb1.g, rgb1.b)
  const l2 = getLuminance(rgb2.r, rgb2.g, rgb2.b)

  return contrastRatio(l1, l2) >= threshold
}

export function capitalize(text: string) {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}
